import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Skeleton } from '@progress/kendo-react-indicators';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import React, { useContext, useEffect, useState } from 'react';
import { getEntityRelations } from '../../../API/EntityRelations/EntityRelations';
import EntityRecommendationContext from '../../../Context/EntityRecommendation/EntityRecommendationContext';
import TYPES from '../../../Context/EntityRecommendation/TYPES';
import { ENTITY_RELATION_PLURAL_LABEL } from '../../Helpers/Labels/EntityRelationLabel';
import Scrollable from '../../Shared/Edit/Scrollable';
import InnerLoader from '../../Shared/Loader/InnerLoader';
import { OverlayLoader } from '../../Shared/Loader/OverlayLoader';
import byEntity1CardinalityAndEntity2 from './sortRelations';
import sortRelations from './sortRelations';
import { useTranslation } from "react-i18next";

function EntityRelationsRecommendation({ usedInSchemaRecommendation, organizationId, projectId, entities, onAdd, onBack }) {
    const { t } = useTranslation();

    const { api, state, dispatch } = useContext(EntityRecommendationContext);

    const importRelations = async (e) => {
        try {
            if (e && e.target) {
                e.target.disabled = true;
            }
            const data = state.relations.filter(relation => relation.selected).map(relation => {
                return {
                    from: relation.from, cardinality: relation.cardinality, to: relation.to,
                    referenceAttribute: relation.referenceAttribute?.properties
                }
            });
            const importedRelations = await api.importEntityRelations(data, projectId)
            onAdd(importedRelations);
            dispatch({
                type: TYPES.SET_CREATING_DATA,
                payload: false
            })
        } catch (err) {
            console.error(err);
        } finally {
            if (e && e.target)
                e.target.disabled = false;
        }
    }

    return (
        <>
            <RecommendedLinkedEntityGrid
                organizationId={organizationId}
                projectId={projectId}
                entities={entities}
                onBack={onBack}
                onAdd={importRelations} />
        </>
    )
}

export function RecommendedLinkedEntityGrid({
    organizationId, projectId, entities, usedInWorkspace,
    addButtonText,
    onBack, onAdd, onNext
}) {
    const { t } = useTranslation();

    const { api, state, dispatch } = useContext(EntityRecommendationContext);

    const [fetchingData, setFetchingData] = useState(true);
    
    useEffect(() => {
        const selectedEntityNames = state.data
                                        .filter(entity => entity.selected)
                                        .map(entity => entity.name.toLowerCase());
        const userEntityNames = entities.map(entity => entity.name.toLowerCase())
        const entityNames = [...selectedEntityNames, ...userEntityNames];
        api.getRecommendedEntityRelations(entityNames, organizationId, projectId)
            .then(data => {
                if (usedInWorkspace) {
                    selectPreviouslySelectedRelations(data);
                }
                const sortedData = data.sort(byEntity1CardinalityAndEntity2);
                dispatch({
                    type: TYPES.RELATIONS_LOADED,
                    payload: sortedData
                })
                setFetchingData(false);
            });
    }, [])

    function selectPreviouslySelectedRelations(data) {
        const previouslySelectedRelations = state.relations.filter(relation => relation.selected);
        if (previouslySelectedRelations.length > 0) {
            data.forEach(relation => {
                const previouslySelectedRelation = previouslySelectedRelations.find(r => (
                    r.from === relation.from && r.to === relation.to && r.cardinality === relation.cardinality
                ));
                if (previouslySelectedRelation) {
                    relation.selected = true;
                }
            })
        }
    }

    if (fetchingData) {
        return (
            <div className='h-100'>
                <div className='h-25'>
                    <Skeleton className='h-100' />
                </div>
                <Grid
                    data={Array.from({ length: 10 }, (() => {}))}>
                    <GridColumn
                        width={30}
                        cell={
                            () => <td><Skeleton /></td>
                        } />
                    <GridColumn
                        cell={
                            () => <td><Skeleton /></td>
                        } />
                    <GridColumn
                        cell={
                            () => <td><Skeleton /></td>
                        } />
                    <GridColumn
                        cell={
                            () => <td><Skeleton /></td>
                        } />
                </Grid>
            </div>
        );
    }

    const handleSelect = (relation) => {
        const { id, selected } = relation;
        const type = selected ? TYPES.RELATION_DESELECTED : TYPES.RELATION_SELECTED;
        dispatch({ type, payload: { id } });
    }

    function anyRelationSelected() {
        return state.relations.some(relation => relation.selected);
    }

    async function handleSubmit() {
        dispatch({
            type: TYPES.SET_CREATING_DATA,
            payload: true
        })
        await onAdd();
    } 

    return (
        <>
            <div style={{ height: "85%" }}>
                <Scrollable>
                    <Grid
                        data={state.relations}>
                        <GridColumn
                            width={40}
                            headerClassName="p-0 padding-left-0"
                            headerCell={() => (
                                <Checkbox
                                    aria-label={t('create_project.alice_recommended_relations.select_all.label')}
                                    value={
                                        state.relations.length > 0 &&
                                        state.relations.every(relation => relation.selected)
                                    }
                                    checked={
                                        state.relations.length > 0 &&
                                        state.relations.every(relation => relation.selected)
                                    }
                                    onChange={(e) => {
                                        const type = e.target.element.checked ? TYPES.RELATIONS_SELECTED : TYPES.RELATIONS_DESELECTED;
                                        dispatch({ type });
                                    }} />
                            )}
                            cell={props => (
                                <td>
                                    <Checkbox
                                        aria-label={(
                                            props.dataItem.from + "-" + props.dataItem.to
                                        )}
                                        value={props.dataItem.selected}
                                        onChange={(e) => handleSelect(props.dataItem)} />
                                </td>
                            )} />
                        <GridColumn
                            title="Object 1"
                            field='from' />
                        <GridColumn
                            title="Cardinality"
                            field='cardinality'
                            headerCell={() => (
                                <Tooltip
                                    position='right'
                                    anchorElement='target'
                                    tooltipStyle={{
                                        width: "300px",
                                    }}>
                                    <span>
                                        Cardinality
                                        &nbsp;
                                        <i
                                            className='fa-solid fa-info-circle'
                                            title={`
                                            An ${ENTITY_RELATION_PLURAL_LABEL} is a link between two objects.
                                            For example, one Account can be linked to many Opportunities in a Sales Management app.
                                            So the cardinality is 1 to M.
                                            `} />
                                    </span>
                                </Tooltip>
                            )}
                            cell={props => (
                                <td>
                                    { props.dataItem.cardinality === "1-*" && t('create_project.alice_recommended_relations.cardinality.one_to_many')}
                                    { props.dataItem.cardinality === "*-*" && t('create_project.alice_recommended_relations.cardinality.many_to_many')}
                                    { props.dataItem.cardinality === "1-1" && t('create_project.alice_recommended_relations.cardinality.one_to_one')}
                            </td>
                            )} />
                        <GridColumn
                            title="Object 2"
                            field='to' />
                    </Grid>
                </Scrollable>
            </div>
            <DialogActionsBar layout='end'>
                { state.creatingData && (
                    <>
                        <OverlayLoader />
                        <InnerLoader borderColor="green" />
                    </>
                )}
                { onBack && <button onClick={onBack} className="btn">{t('create_project.alice_recommended_relations.back_button')}</button> }
                { onBack && <>&nbsp;</> }
                { 
                    usedInWorkspace &&
                    <button disabled={state.creatingData} onClick={handleSubmit} className="btn btn-primary">
                        { addButtonText || t('create_project.alice_recommended_relations.create_app') }
                    </button>
                }
                { 
                    !usedInWorkspace &&
                    <button disabled={state.creatingData || !anyRelationSelected()} onClick={handleSubmit} className='btn btn-primary'>
                        { addButtonText || t('create_project.alice_recommended_relations.add') }
                    </button>
                }
            </DialogActionsBar>
        </>
    )
}

RecommendedLinkedEntityGrid.defaultProps = {
    entities: []
}

export default EntityRelationsRecommendation;